import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, BadgeCheck, MapPin, Sparkles } from "lucide-react";
import Logo from "./Logo";

/**
 * AuthLayout — split-screen shell for vendor auth pages.
 *
 * Left: inset brand panel in the primary gradient with feature cards
 *       (hidden below lg).
 * Right: the form sits in a raised white card on a soft tinted background.
 */
const HIGHLIGHTS = [
  { icon: BadgeCheck, text: "Free to join, no setup fees" },
  { icon: MapPin, text: "Reach customers across 500+ cities" },
  { icon: Sparkles, text: "AI tools to grow your bookings" },
];

const AuthLayout = ({ children }) => {
  const year = new Date().getFullYear();

  return (
    <div className="relative flex min-h-screen bg-gradient-to-br from-purple-50/70 via-gray-50 to-pink-50/70 font-['Inter']">
      {/* Brand panel — inset, with rounded corners */}
      <aside className="hidden w-[44%] max-w-xl p-4 lg:block">
        <div className="relative flex h-full min-h-[calc(100vh-2rem)] flex-col justify-between overflow-hidden rounded-[2rem] bg-gradient-to-br from-purple-600 via-purple-600 to-pink-600 p-10 text-white shadow-2xl shadow-purple-600/25 xl:p-12">
          <div className="pointer-events-none absolute -right-24 -top-24 h-80 w-80 rounded-full bg-white/10 blur-2xl" />
          <div className="pointer-events-none absolute -bottom-32 -left-20 h-96 w-96 rounded-full bg-pink-300/20 blur-3xl" />
          {/* Fine grid texture */}
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.07]"
            style={{
              backgroundImage:
                "linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)",
              backgroundSize: "44px 44px",
            }}
          />

          <Link to="/" aria-label="MandapAI home" className="relative self-start">
            <Logo size={48} />
          </Link>

          <div className="relative">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider ring-1 ring-inset ring-white/20">
              Vendor portal
            </span>
            <h2 className="mt-4 font-['Manrope'] text-4xl font-extrabold leading-[1.1] tracking-tight xl:text-[2.75rem]">
              Grow your event business with MandapAI
            </h2>
            <ul className="mt-8 space-y-3">
              {HIGHLIGHTS.map(({ icon: Icon, text }) => (
                <li
                  key={text}
                  className="flex items-center gap-3.5 rounded-2xl bg-white/10 p-3.5 text-[15px] font-medium text-white ring-1 ring-inset ring-white/15 backdrop-blur-sm"
                >
                  <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-white/15 ring-1 ring-inset ring-white/20">
                    <Icon className="h-5 w-5" />
                  </span>
                  {text}
                </li>
              ))}
            </ul>
          </div>

          <p className="relative text-xs text-white/70">
            © {year} MandapAI · India's event marketplace
          </p>
        </div>
      </aside>

      {/* Form side */}
      <div className="relative flex min-w-0 flex-1 flex-col">
        <header className="flex items-center justify-between px-5 py-5 sm:px-8">
          <Link to="/" aria-label="MandapAI home" className="lg:invisible">
            <Logo size={32} />
          </Link>
          <Link
            to="/"
            className="inline-flex h-10 items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3.5 text-sm font-semibold text-gray-700 shadow-sm shadow-gray-900/[0.03] transition-colors hover:border-purple-200 hover:bg-purple-50"
          >
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>
        </header>

        <main className="flex flex-1 items-center justify-center px-4 py-6 sm:px-8">
          <div className="w-full max-w-[30rem] rounded-3xl border border-gray-200/70 bg-white p-6 shadow-xl shadow-purple-900/[0.06] sm:p-9">
            {children}
          </div>
        </main>

        <footer className="px-5 py-6 text-center text-xs text-gray-400 sm:px-8 lg:hidden">
          © {year} MandapAI · India's event marketplace
        </footer>
      </div>
    </div>
  );
};

/** Shared field styles for auth forms: roomy 48px inputs, soft focus glow. */
export const fieldClass = (hasError) =>
  `h-12 w-full rounded-xl border pl-11 pr-4 text-[15px] text-gray-900 placeholder-gray-400 transition-all duration-200 focus:bg-white focus:outline-none focus:ring-4 ${
    hasError
      ? "border-red-400 bg-red-50/40 focus:border-red-500 focus:ring-red-500/10"
      : "border-gray-200 bg-gray-50/70 hover:border-gray-300 focus:border-purple-500 focus:ring-purple-500/10"
  }`;

export const labelClass = "mb-1.5 block text-[13px] font-semibold text-gray-700";

/** Leading icon inside a field; its wrapper needs `group/field relative`. */
export const fieldIconClass =
  "pointer-events-none absolute left-4 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-gray-400 transition-colors group-focus-within/field:text-purple-600";

/** Small pill above a page title, e.g. "Vendor sign in" or "Step 1 of 3". */
export const AuthEyebrow = ({ icon: Icon, children }) => (
  <span className="mb-4 inline-flex items-center gap-1.5 rounded-full border border-purple-200 bg-purple-50 px-3 py-1 text-xs font-semibold text-gray-800">
    {Icon && <Icon className="h-3.5 w-3.5 text-purple-600" />}
    {children}
  </span>
);

export default AuthLayout;
